// lib/storage/sync-event-emitter.ts
/**
 * Event emitter for sync completion notifications and data freshness tracking
 * Allows components to listen for when specific keys are synced and track data source
 */

type SyncEventListener = (key: string, success: boolean) => void;
type DataSourceListener = (key: string, source: 'cache' | 'remote', timestamp: number) => void;

class SyncEventEmitter {
  private listeners: Map<string, Set<SyncEventListener>> = new Map();
  private dataSourceListeners: Map<string, Set<DataSourceListener>> = new Map();

  /**
   * Subscribe to sync completion events for a specific key pattern
   */
  on(keyPattern: string, listener: SyncEventListener): () => void {
    if (!this.listeners.has(keyPattern)) {
      this.listeners.set(keyPattern, new Set());
    }

    this.listeners.get(keyPattern)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(keyPattern);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(keyPattern);
        }
      }
    };
  }

  /**
   * Subscribe to data source events (cache vs remote)
   */
  onDataSource(keyPattern: string, listener: DataSourceListener): () => void {
    if (!this.dataSourceListeners.has(keyPattern)) {
      this.dataSourceListeners.set(keyPattern, new Set());
    }

    this.dataSourceListeners.get(keyPattern)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.dataSourceListeners.get(keyPattern);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.dataSourceListeners.delete(keyPattern);
        }
      }
    };
  }

  /**
   * Emit sync completion event
   */
  emit(key: string, success: boolean): void {
    // Notify exact match listeners
    const exactListeners = this.listeners.get(key);
    if (exactListeners) {
      exactListeners.forEach((listener) => listener(key, success));
    }

    // Notify pattern match listeners (e.g., "completed-workouts:*")
    this.listeners.forEach((listeners, pattern) => {
      if (pattern.endsWith("*")) {
        const prefix = pattern.slice(0, -1);
        if (key.startsWith(prefix)) {
          listeners.forEach((listener) => listener(key, success));
        }
      }
    });
  }

  /**
   * Emit data source event (for tracking cache vs remote fetches)
   */
  emitDataSource(key: string, source: 'cache' | 'remote', timestamp: number = Date.now()): void {
    // Notify exact match listeners
    const exactListeners = this.dataSourceListeners.get(key);
    if (exactListeners) {
      exactListeners.forEach((listener) => listener(key, source, timestamp));
    }

    // Notify pattern match listeners (e.g., "athlete:*", or "*" for all)
    this.dataSourceListeners.forEach((listeners, pattern) => {
      if (pattern === "*") {
        // Global wildcard - matches everything
        listeners.forEach((listener) => listener(key, source, timestamp));
      } else if (pattern.endsWith("*")) {
        // Prefix wildcard (e.g., "athlete:*")
        const prefix = pattern.slice(0, -1);
        if (key.startsWith(prefix)) {
          listeners.forEach((listener) => listener(key, source, timestamp));
        }
      }
    });
  }

  /**
   * Remove all listeners
   */
  clear(): void {
    this.listeners.clear();
    this.dataSourceListeners.clear();
  }
}

export const syncEventEmitter = new SyncEventEmitter();
